/**
 * @fileoverview Notifications API calls.
 * @description Talks to this project's own backend — never Firebase's Admin APIs directly — to
 * register the current browser's FCM device token and to list and mark the signed-in user's in-app
 * notifications. Whether a request is answered by a real backend or the local mock adapter (see
 * mocks/) is transparent to callers. The backend has no "mark all as read" endpoint, so
 * markAllNotificationsRead marks each given notification through the per-notification endpoint.
 * POST /notifications/device-tokens is not in the backend's API contract yet — only the mock
 * answers it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

export interface RegisterDeviceTokenRequest {
  token: string
  platform: 'web'
}

export type InAppNotificationStatus = 'UNREAD' | 'READ' | 'ARCHIVED' | 'DISMISSED'

/** Where the notification leads when opened, e.g. `{ targetType: 'REPORT_DETAILS', ... }`. */
export type NotificationNavigationTarget = Record<string, unknown>

/** One in-app notification delivered to the signed-in user (InAppNotificationResponseDto). */
export interface InAppNotification {
  /** The per-recipient delivery id — the id the read endpoint takes. */
  notificationDeliveryId: string
  notificationId: string
  title?: string
  body: string
  inAppStatus: InAppNotificationStatus
  navigationTarget?: NotificationNavigationTarget
  createdAt: string
  readAt: string | null
}

export interface MarkNotificationReadResponse {
  notificationDeliveryId: string
  inAppStatus: 'READ' | 'ARCHIVED'
  readAt: string
}

export interface UnreadNotificationCountResponse {
  unreadCount: number
}

/**
 * @description Registers the current browser's FCM device token with the backend, so it can be
 * targeted by future notifications sent via the Firebase Admin SDK.
 * @param {RegisterDeviceTokenRequest} payload - The device token to register.
 */
export async function registerDeviceToken(payload: RegisterDeviceTokenRequest): Promise<void> {
  await httpClient.post('/notifications/device-tokens', payload)
}

/**
 * @description Fetches the signed-in user's in-app notifications.
 * @returns {Promise<InAppNotification[]>} The notifications.
 */
export async function getNotificationHistory(): Promise<InAppNotification[]> {
  const { data } = await httpClient.get<InAppNotification[]>('/notifications')
  return data
}

/**
 * @description Fetches the exact number of the signed-in user's unread notifications, for the bell
 * badge. GET /notifications is paginated, so counting its first page would cap the badge.
 * @returns {Promise<number>} The unread count.
 */
export async function getUnreadNotificationCount(): Promise<number> {
  const { data } = await httpClient.get<UnreadNotificationCountResponse>(
    '/notifications/unread-count',
  )
  return data.unreadCount
}

/**
 * @description Marks one notification as read.
 * @param {string} notificationDeliveryId - The notification's delivery id.
 * @returns {Promise<MarkNotificationReadResponse>} The notification's new status and read time.
 */
export async function markNotificationRead(
  notificationDeliveryId: string,
): Promise<MarkNotificationReadResponse> {
  const { data } = await httpClient.post<MarkNotificationReadResponse>(
    `/notifications/${notificationDeliveryId}/read`,
  )
  return data
}

/**
 * @description Marks each of the given notifications as read, in parallel.
 * @param {string[]} notificationDeliveryIds - The delivery ids of the notifications to mark.
 */
export async function markAllNotificationsRead(notificationDeliveryIds: string[]): Promise<void> {
  await Promise.all(notificationDeliveryIds.map((id) => markNotificationRead(id)))
}
